// ═══════════════════════════════════════════════════════════════════
// willieModel3D.jsx — WILLIE, SCULPTED   (caP'n, 2026-09-16, THE PLAZA UPDATE #1)
// ═══════════════════════════════════════════════════════════════════
// V'ael's Champion and groundskeeper, off his front art: the mossy turtle
// head, the huge sad brown eyes, the torn leather vest over the grey
// shirt, the buckle, the two pouches (the teal one's W glows), the patched
// shorts, the bare feet — and THE BOOK, open in his left hand. He reads as
// he walks; now and then, standing still, he looks up and points at you
// (the art's own pose). 3D only; 2D screens keep their pictures.
// `public/models/willie.glb`, built in the Cowork sandbox (builder in
// _scratch/willie3d). Same contract as laostreModel3D.jsx.
// The painted WillyBody stays in plazaFolk3D.jsx as the fail-safe.
// ⭐ 2026-09-23 — THE BLENDER PASS (the PLAZA's Willie only). willie.glb is now
// built by gamemaster/models/sculpt/builders/willie_blender: a new head off
// caP'n's three face pictures (heavy tired lids over near-black-brown irises,
// bag rolls, snout pad + teardrop nostrils, a downturned mouth, leaf moss on a
// crown mat, grey pebbles) on v1's SAME skeleton, bone names and READ_POSE, so
// williePose drives it unchanged. New part name: wil_pebbles (rides the head,
// takes the ink). Its crown measures 1.34 (v1's spiky moss reached 1.40); MODEL_TOP
// stays 1.4 so the BODY keeps its v1 size. The Forge keeps willie_forge.glb (v1).
// ⭐ 2026-09-24 — HIS FACES (caP'n: "give him the love and attention he deserves",
// off his five pictures). willie.glb now carries MORPH TARGETS on the face
// parts (builder: willie_blender/wil_expr.py) — blink, wide, squint, brows_sad,
// brows_up, smile, frown, mouth_open, mouth_o — and a NEW part, wil_tears, with
// its own morph tears_in (1 = drawn back into the lids). Nothing was re-sculpted:
// the signed-off skeleton face IS "neutral". Drive it with
//   ctl.expr  = "neutral" | "sad" | "teary_smile" | "smile" | "shocked" (WILLIE_EXPRESSIONS)
//   ctl.talk  = true  → the mouth flaps (dialogue)
//   ctl.point = true / false → hold / never point (undefined = his own ~11 s point)
//   ctl.look  = true  → he looks up at you (off the book) without pointing
// He blinks on his own. Review room: ?expr=shocked&talk=1&point=1
// ⭐ 2026-09-25 — HIS HANDS (caP'n: "one more proper rigging pass … fingers limbs etc … so we can do
// all kinds of expressions … just the ability"). willie.glb has 39 joints now: every finger two
// bones + the thumb, both hands (index1R … thumb2L), on NEW relaxed hands (part wil_hands; v1's
// sculpted-shut hands are sunk inside them). willieActing.js carries the abilities — nothing
// plays unless a scene asks:
//   ctl.hands = { R: "fist" | "open" | "point" | "grip" | "book" | "thumbs_up" | "claw" | "relaxed", L: … }
//   ctl.act   = "wave" | "present" | "beckon" | "shrug" | "thumbs_up" | "facepalm" | "wipe_tear" | "sweep"
//   ctl.book  = false (puts the book away)      ctl.hold = "broom" (the broom in his right fist)
// Defaults are the signed-off look: right fist (or the point), left hand on the book.
// Review room: ?act=wave&hands=R:open&book=0&hold=broom&rot=forearmR:-2,0,0
// ═══════════════════════════════════════════════════════════════════
import { Component, Suspense, useMemo, useRef } from "react";
import { useFrame, useLoader } from "@react-three/fiber";
import * as THREE from "three";
import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader.js";
import { clone as cloneSkinned } from "three/examples/jsm/utils/SkeletonUtils.js";
import { actingState, actWillie, makeBroom, HAND_SHAPES } from "./willieActing";   // his hands, his acts, the broom (2026-09-25)

export const WILLIE_GLB_URL = `${process.env.PUBLIC_URL || ""}/models/willie.glb`;
// ⭐ THE FORGE WILLIE (caP'n, 2026-09-23): "leave the willie's model that is in the Forge alone".
// willie_forge.glb is a FROZEN copy of the 2026-09-16 sculpt (sad tired eyes and all);
// the Plaza Willie gets the Blender treatment, the Forge keeps this one. Never rebuild it.
export const WILLIE_FORGE_GLB_URL = `${process.env.PUBLIC_URL || ""}/models/willie_forge.glb`;
const MODEL_TOP = 1.4;   // the 2026-09-16 sculpt's crown (willie.glb until the Blender pass lands, willie_forge.glb forever)
const NO_INK = /eyes|buckle|glyph|bookw|bodymoss|cuffs|mouth/;   // wil_mouth (2026-09-24): the mouth's inside never takes an outline
const SELF_LIGHT = 0.4;
const GLYPH = 0x7ff0ff;               // the W on his pouch

let RAMP = null;
const toonRamp = () => {
  if (RAMP) return RAMP;
  RAMP = new THREE.DataTexture(new Uint8Array([172, 172, 172, 255, 228, 228, 228, 255, 255, 255, 255, 255]), 3, 1);
  RAMP.minFilter = RAMP.magFilter = THREE.NearestFilter;
  RAMP.needsUpdate = true;
  return RAMP;
};
let INK = null;
const inkMat = () => {
  if (INK) return INK;
  INK = new THREE.MeshBasicMaterial({ color: 0x161114, side: THREE.BackSide });
  INK.onBeforeCompile = (sh) => {
    sh.vertexShader = sh.vertexShader.replace("#include <begin_vertex>", "#include <begin_vertex>\n  transformed += normalize( normal ) * 0.0030;");
  };
  return INK;
};

// the HANDS' outline fades out up the forearm stub (2026-09-25): the stub tucks under the arm's own skin
// just above the wrist, and a full-width hull there drew a hairline ring round his wrist
let INK_HANDS = null;
const inkHandsMat = () => {
  if (INK_HANDS) return INK_HANDS;
  INK_HANDS = new THREE.MeshBasicMaterial({ color: 0x161114, side: THREE.BackSide });
  INK_HANDS.onBeforeCompile = (sh) => {
    sh.vertexShader = sh.vertexShader.replace("#include <begin_vertex>", "#include <begin_vertex>\n  transformed += normalize( normal ) * 0.0030 * ( 1.0 - smoothstep( 0.505, 0.522, position.y ) );");
  };
  return INK_HANDS;
};

function prepare(scene) {
  const root = cloneSkinned(scene);
  const meshes = [];
  root.traverse((o) => { if (o.isMesh) meshes.push(o); });
  let glyph = null;
  meshes.forEach((o) => {
    const m = o.material;
    if (m.map) m.map.colorSpace = THREE.SRGBColorSpace;
    const vc = !!o.geometry.attributes.color;
    o.material = new THREE.MeshToonMaterial({
      color: m.color, map: m.map, gradientMap: toonRamp(), side: m.side, vertexColors: vc,
      emissive: m.color.clone().multiplyScalar(SELF_LIGHT), emissiveMap: m.map || null,
    });
    // vertex-coloured parts keep their colour in the vertices: the self-light
    // has to be scaled by it too, or it washes them out to grey
    if (vc) o.material.onBeforeCompile = (sh) => {
      sh.fragmentShader = sh.fragmentShader.replace("vec3 totalEmissiveRadiance = emissive;", "vec3 totalEmissiveRadiance = emissive * vColor.rgb;");
    };
    o.frustumCulled = false;
    if (o.name === "wil_tears") {
      // glossy: blue body + white highlight in the vertices, lit mostly by itself (the art's tears glow)
      o.material = new THREE.MeshToonMaterial({ color: 0xffffff, vertexColors: true, gradientMap: toonRamp(), emissive: 0xffffff, emissiveIntensity: 0.45 });
      o.material.onBeforeCompile = (sh) => { sh.fragmentShader = sh.fragmentShader.replace("vec3 totalEmissiveRadiance = emissive;", "vec3 totalEmissiveRadiance = emissive * vColor.rgb;"); };
    }
    if (o.name === "wil_mouth") o.material = new THREE.MeshBasicMaterial({ color: 0x2a0d0e, side: THREE.DoubleSide });
    if (o.name === "wil_glyph") {
      glyph = o;
      o.material = new THREE.MeshBasicMaterial({ color: GLYPH, toneMapped: false });
      return;
    }
    if (NO_INK.test(o.name)) return;
    const im = o.name === "wil_hands" ? inkHandsMat() : inkMat();
    const h = o.isSkinnedMesh ? new THREE.SkinnedMesh(o.geometry, im) : new THREE.Mesh(o.geometry, im);
    if (o.isSkinnedMesh) h.bind(o.skeleton, o.bindMatrix);
    h.name = o.name + "_ink"; h.frustumCulled = false;
    if (o.morphTargetInfluences) h.morphTargetInfluences = o.morphTargetInfluences;   // the outline wears the same face
    o.userData.ink = h;
    o.parent.add(h);
  });
  // the faces: every mesh with morph targets, and the tears
  const faces = meshes.filter((o) => o.morphTargetDictionary);   // the tears too: they ride the lids
  const tears = meshes.find((o) => o.name === "wil_tears") || null;
  const B = {};
  root.traverse((o) => { if (o.isBone) B[o.name] = o; });
  if (B.hips) B.hips.userData.y0 = B.hips.position.y;
  // the book (it can be put away: ctl.book = false) and the broom (ctl.hold = "broom")
  const books = meshes.filter((o) => /^wil_(book|pages)/.test(o.name));
  const broom = makeBroom(inkMat()); root.add(broom);
  return { root, B, glyph, faces, tears, books, broom };
}

// ─── THE EXPRESSIONS (off caP'n's five pictures, 2026-09-24) ───
// weights per morph; `tears` 0..1 is how far they have run. Neutral is the signed-off face.
export const WILLIE_EXPRESSIONS = {
  neutral:     {},
  sad:         { brows_sad: 1, frown: 1, wide: 0.6, tears: 1 },                     // the puppy-dog eyes. The big ones.
  teary_smile: { brows_sad: 0.8, smile: 1, wide: 0.3, squint: 0.6, tears: 1 },
  smile:       { smile: 1, squint: 0.65, wide: 0.3, brows_up: 0.25 },
  shocked:     { wide: 1, brows_up: 1, mouth_open: 1, mouth_o: 0.9 },             // bulging — but not THAT much
};
const FACE_KEYS = ["blink", "wide", "squint", "brows_sad", "brows_up", "smile", "frown", "mouth_open", "mouth_o"];

// ⚠ upperarmL / forearmL / handL at rest MUST match READ_POSE in the builder
// (build.py): the book is authored in that pose, so it only sits in his
// hand while these three numbers agree.
export function williePose(B, t, ph, amt, pt, lk = pt) {
  const set = (n, x, y, z) => { const b = B[n]; if (b) b.rotation.set(x || 0, y || 0, z || 0); };
  const sw = Math.sin(ph);
  for (const [k, s, off] of [["L", 1, 0], ["R", -1, Math.PI]]) {
    const lp = ph + off, fwd = Math.sin(lp), swing = Math.max(0, Math.cos(lp));
    set("thigh" + k, (-fwd * 0.38 - swing * 0.14) * amt, 0, s * 0.04);
    set("shin" + k, swing * 0.7 * amt, 0, 0);
    set("foot" + k, (-swing * 0.3 + Math.max(0, -fwd) * 0.2) * amt, s * 0.05, 0);
  }
  // the book arm: up in front of his chest, a small bob with the step
  const bob = Math.sin(ph * 2) * 0.03 * amt + Math.sin(t * 0.9) * 0.015;
  set("upperarmL", -0.42 + bob, 0.2, -0.1);
  set("forearmL", -1.05 - bob, 0.35, 0);
  set("handL", 0.15, -0.5, -0.25);
  // the free arm: swings with the walk, or lifts to point
  const fr = -Math.sin(ph) * 0.35 * amt;
  set("upperarmR", fr * (1 - pt) - 1.1 * pt, 0.1 * pt, -0.08 + 0.3 * pt);
  set("forearmR", (-0.15 + Math.min(0, fr) * 0.4) * (1 - pt) - 0.35 * pt, 0, 0);
  set("handR", -0.2 * pt, 0, 0);
  // his index has its own bone: folded into the fist unless he's pointing
  set("indexR", -2.3 * (1 - pt), 0, 0);
  if (B.indexR) B.indexR.scale.setScalar(1 - 0.45 * (1 - pt));
  set("hips", 0, sw * 0.07 * amt, Math.sin(ph) * 0.03 * amt);
  if (B.hips) B.hips.position.y = B.hips.userData.y0 + (Math.abs(Math.cos(ph)) - 0.6) * 0.022 * amt;
  set("spine", 0.03 + 0.03 * amt, -sw * 0.05 * amt, -Math.sin(ph) * 0.02 * amt);
  set("chest", -0.02 + Math.sin(t * 1.1) * 0.012, -sw * 0.04 * amt, 0);
  set("neck", 0.06, 0, 0);
  // he looks down at the book, and up at you when he points
  set("head", 0.22 * (1 - lk) - 0.05 + Math.sin(t * 0.6) * 0.02, 0.18 * (1 - lk) + Math.sin(t * 0.3) * 0.08, 0.04 * (1 - lk));
}

const POINT_EVERY = 11, POINT_FOR = 2.6;   // seconds
const smooth = (x) => { x = Math.min(1, Math.max(0, x)); return x * x * (3 - 2 * x); };

export function WillieModel({ ctl, h = 1.4, url = WILLIE_GLB_URL, top = MODEL_TOP }) {
  const gltf = useLoader(GLTFLoader, url);
  const { root, B, glyph, faces, tears, books, broom } = useMemo(() => prepare(gltf.scene), [gltf]);
  const AS = useRef(null); if (!AS.current) AS.current = actingState();
  const g = useRef(); const yaw = useRef(0); const ph = useRef(0); const amt = useRef(0); const pt = useRef(0); const lk = useRef(0);
  const qs = typeof window !== "undefined" ? window.location.search : "";
  const force = /[?&]point=1/.test(qs);   // review room: hold the point
  const qExpr = (qs.match(/[?&]expr=([a-z_]+)/) || [])[1];
  const qTalk = /[?&]talk=1/.test(qs);
  const snap = /[?&]snap=1/.test(qs);   // review room: jump straight to the face (no easing)
  // review room: ?face=blink:1,smile:0.5 dials raw morphs (overrides expr)
  const qFace = (qs.match(/[?&]face=([a-z_:.,0-9]+)/) || [])[1];
  // review room: ?act=wave&hands=R:open,L:fist&book=0&hold=broom
  const qAct = (qs.match(/[?&]act=([a-z_]+)/) || [])[1];
  const qHands = (qs.match(/[?&]hands=([A-Za-z_:,]+)/) || [])[1];
  const qHandsObj = qHands ? Object.fromEntries(qHands.split(",").map((kv) => kv.split(":")).filter(([k, v]) => HAND_SHAPES[v])) : null;
  const qBook = /[?&]book=0/.test(qs) ? false : undefined;
  const qHold = (qs.match(/[?&]hold=([a-z]+)/) || [])[1];
  // review room: ?rot=forearmR:-2,0,0.1;handR:0,1,0 sets raw bone rotations last (for dialling a new pose)
  const qRot = ((qs.match(/[?&]rot=([A-Za-z0-9_:.,;-]+)/) || [])[1] || "").split(";").filter(Boolean).map((kv) => { const [n, v] = kv.split(":"); return [n, (v || "").split(",").map(Number)]; });
  const faceOverride = qFace ? Object.fromEntries(qFace.split(",").map((kv) => { const [k, v] = kv.split(":"); return [k, +v]; })) : null;
  // the face's live state: current weights, blink clock, tears, the shock jolt
  const F = useRef(null);
  if (!F.current) F.current = { w: Object.fromEntries(FACE_KEYS.map((k) => [k, 0])), tears: 0, blinkAt: 2 + Math.random() * 3, blink: 0, expr: "neutral", shockT: -9, talkPh: 0 };
  useFrame(({ clock }, dtRaw) => {
    const o = g.current; if (!o) return;
    const dt = Math.min(dtRaw || 0.016, 0.05); const t = clock.getElapsedTime() + 1.3;
    const f = (ctl && ctl.facing) || { x: 0, y: 1 };
    const want = Math.atan2(f.x || 0, f.y === undefined ? 1 : f.y);
    let d = want - yaw.current; while (d > Math.PI) d -= Math.PI * 2; while (d < -Math.PI) d += Math.PI * 2;
    yaw.current += d * Math.min(1, dt * 9);
    o.rotation.y = yaw.current;
    const speed = (ctl && ctl.speed) || 0;
    amt.current += ((speed ? 1 : 0) - amt.current) * Math.min(1, dt * 6);
    ph.current += (speed ? 1 : amt.current) * dt * 7;
    // the point: a slow rise and fall inside its window, never while walking
    const c = t % POINT_EVERY;
    const cp = ctl && ctl.point;
    const want_pt = (force || cp === true) ? 1 : cp === false ? 0 : smooth(c / 0.5) * smooth((POINT_FOR - c) / 0.5) * (1 - amt.current);
    pt.current += (want_pt - pt.current) * Math.min(1, dt * 8);
    // ── the face ──
    const S = F.current;
    const exprName = (ctl && ctl.expr) || qExpr || "neutral";
    const E = faceOverride || WILLIE_EXPRESSIONS[exprName] || WILLIE_EXPRESSIONS.neutral;
    if (exprName !== S.expr) { if (exprName === "shocked") S.shockT = t; S.expr = exprName; }
    const shocked = exprName === "shocked";
    const rate = snap ? 1 : Math.min(1, dt * (shocked ? 18 : 7));          // a shock SNAPS on; everything else eases
    for (const k of FACE_KEYS) S.w[k] += ((E[k] || 0) - S.w[k]) * rate;
    // tears well up slowly and draw back a little faster
    const wantT = E.tears || 0;
    S.tears += (wantT - S.tears) * (snap ? 1 : Math.min(1, dt * (wantT > S.tears ? 0.9 : 2.2)));
    // the blink: every 2.5–5.5 s, ~0.16 s, now and then a double; a shocked stare hardly blinks
    if (t > S.blinkAt && !faceOverride) {
      const k = (t - S.blinkAt) / 0.16;
      S.blink = k < 1 ? Math.sin(k * Math.PI) : 0;
      if (k >= 1) S.blinkAt = t + (shocked ? 5 : 2.5) + Math.random() * 3 - (Math.random() < 0.15 ? 2.6 : 0) * (shocked ? 0 : 1);
    }
    // talking: syllables, not a sine
    let talk = 0;
    if ((ctl && ctl.talk) || qTalk) { S.talkPh += dt * 18; talk = Math.max(0, Math.sin(S.talkPh) * 0.6 + Math.sin(S.talkPh * 0.37 + 1.3) * 0.4) * 0.7; }
    const W = { ...S.w };
    W.blink = Math.min(1, W.blink + S.blink * (1 - 0.6 * W.wide));
    W.mouth_open = Math.min(1, W.mouth_open + talk * (1 - W.mouth_open));
    W.tears_in = 1 - S.tears;
    for (const m of faces) {
      const d = m.morphTargetDictionary, inf = m.morphTargetInfluences;
      for (const k in d) inf[d[k]] = W[k] || 0;
    }
    if (tears) {
      const tin = W.tears_in;
      const vis = tin < 0.985; tears.visible = vis; if (tears.userData.ink) tears.userData.ink.visible = vis;
      tears.material.emissiveIntensity = 0.42 + Math.sin(t * 3.1) * 0.06;   // a wet shimmer
    }
    // he looks up at you to point, when told to, or when his face has something to say
    const wantLk = Math.max(want_pt, AS.current.lookUp || 0, ((ctl && ctl.look) || exprName !== "neutral") ? 1 : 0) * (1 - amt.current);
    lk.current += (wantLk - lk.current) * Math.min(1, dt * 6);
    williePose(B, t, ph.current, amt.current, pt.current, Math.max(pt.current, lk.current));
    // ── his hands, the acts, the book, the broom (willieActing.js) ──
    const eff = { act: (ctl && ctl.act) || qAct, hands: (ctl && ctl.hands) || qHandsObj, book: ctl && ctl.book !== undefined ? ctl.book : qBook, hold: (ctl && ctl.hold) || qHold };
    const ar = actWillie(B, AS.current, eff, t, dt, ph.current, amt.current, pt.current, root, broom);
    AS.current.lookUp = ar.lookUp;
    for (const [n, r] of qRot) if (B[n]) B[n].rotation.set(r[0] || 0, r[1] || 0, r[2] || 0);
    const bookOn = AS.current.book > 0.5;
    for (const m of books) { m.visible = bookOn; if (m.userData.ink) m.userData.ink.visible = bookOn; }
    // the body keeps the face company: a shock jolts him back, sad droops, a smile lifts
    const js = t - S.shockT, jolt = js >= 0 && js < 0.9 ? Math.sin(Math.min(1, js / 0.12) * Math.PI * 0.5) * Math.exp(-js * 4.5) : 0;
    const add = (n, x, y, z) => { const b = B[n]; if (b) { b.rotation.x += x; b.rotation.y += y; b.rotation.z += z; } };
    add("head", -0.22 * jolt + 0.07 * S.w.brows_sad - 0.03 * S.w.smile, 0, 0.07 * S.w.brows_sad * (1 - S.w.smile) + 0.03 * S.w.smile * Math.sin(t * 1.3));
    add("chest", -0.10 * jolt + 0.04 * S.w.brows_sad - 0.03 * S.w.smile, 0, 0);   // a smile lifts his chest a touch
    add("spine", -0.05 * jolt, 0, 0);
    if (B.hips) B.hips.position.y += 0.012 * jolt + 0.006 * S.w.smile * Math.max(0, Math.sin(t * 4.2)) * (1 - amt.current);   // and a happy little bounce
    if (glyph) glyph.material.color.setHex(GLYPH).multiplyScalar(0.85 + Math.sin(t * 2.4) * 0.15);
  });
  return (
    <group ref={g}>
      <primitive object={root} scale={h / top} />
    </group>
  );
}

class GlbBoundary extends Component {
  constructor(p) { super(p); this.state = { bad: false }; }
  static getDerivedStateFromError() { return { bad: true }; }
  componentDidCatch(e) { console.warn("[willie] willie.glb did not load — the painted body stands in", e); }
  render() { return this.state.bad ? this.props.fallback : this.props.children; }
}

export function WillieSculpted({ ctl, h, Fallback, url, top }) {
  const fb = Fallback ? <Fallback ctl={ctl} h={h} /> : null;
  return (
    <GlbBoundary fallback={fb}>
      <Suspense fallback={fb}>
        <WillieModel ctl={ctl} h={h} url={url} top={top} />
      </Suspense>
    </GlbBoundary>
  );
}

// the Forge's own Willie — the frozen 2026-09-16 body
export function WillieForgeSculpted(p) { return <WillieSculpted {...p} url={WILLIE_FORGE_GLB_URL} top={MODEL_TOP} />; }

export default WillieModel;
